import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { Box, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { timeFormatter } from '@act/shared/utilities';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { filesRequests } from 'redux/requests/files.requests';
import { TemporaryLinkResponse } from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';

interface AudioPlayerProps {
  fileId?: string;
  tempFile?: TemporaryLinkResponse;
  alwaysShowDownload?: boolean;
  mimeType?: string;
}

const AudioPlayer = ({
  fileId,
  tempFile,
  alwaysShowDownload,
  mimeType,
}: AudioPlayerProps) => {
  const [signedUrl, _loadingSignedUrl, getSignedUrl] = useApiCall(
    filesRequests.getSignedUrl
  );

  const defaultPlayerState = {
    tempFile,
    fileId,
    url: null,
    setup: false,
    error: false,
    ready: false,
    playing: false,
    hasExpired: false,
    timePlayed: '--:--',
    timeRemaining: '--:--',
    played: 0,
    loaded: 0,
    duration: 0,
    seeking: false,
  };

  // expiration timeout handler
  const [playerState, setPlayerState] = useState(defaultPlayerState);
  const safeSetPlayerState = newState => {
    setPlayerState({ ...playerState, ...newState });
  };
  const { setup, playing, played, error, duration, ready } = playerState;

  // These are the formats that are supported by the browser.
  const isSupportedFormat = (mimeType: string) => {
    const supportedFormats = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/x-wav',
      'audio/m4a',
      'audio/aac',
      'audio/mp4', // M4A files often use this MIME type
      'audio/ogg',
      'audio/webm',
      'audio/3gpp',
      'audio/3gpp2',
      'audio/amr',
      'audio/amr-nb',
      'audio/flac',
      'audio/x-ms-wma',
    ];
    return supportedFormats.includes(mimeType);
  };
  const isAMRFormat = isSupportedFormat(mimeType);
  const disabled = error || (!!mimeType && !isAMRFormat);

  const playerRef = React.createRef<ReactPlayer>();

  let expirationTimeoutHandler;
  const setExpirationTimeout = (expiresAt?) => {
    const expireValue = expiresAt || tempFile?.expires;
    if (!expireValue) {
      clearTimeout(expirationTimeoutHandler);
      return;
    }

    // Set timeout to refresh the signed url 10 seconds before expiration
    // This is to ensure that the player does not stop playing before
    // the new url is fetched
    const expiresAtTimeout = expireValue - 10;
    // Clear any existing timeout
    if (expirationTimeoutHandler) {
      clearTimeout(expirationTimeoutHandler);
    }

    // Abort attempts to set a timeout if the expiration time is in the past
    if (expiresAtTimeout < 0) {
      const id = tempFile?.file.id || fileId;
      clearTimeout(expirationTimeoutHandler);

      // Get new signed url now
      getSignedUrl({
        params: { id },
      });
      return;
    }

    expirationTimeoutHandler = setTimeout(() => {
      const id = tempFile?.file.id || fileId;
      getSignedUrl({
        params: { id },
      });
    }, expiresAtTimeout);
  };

  useEffect(() => {
    // clear expiration timeout on unmount
    return () => {
      if (expirationTimeoutHandler) {
        clearTimeout(expirationTimeoutHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (!played) {
      return;
    }

    const timePlayed = timeFormatter.millisToDurationString(
      played * duration * 1000
    );
    const timeRemaining = timeFormatter.millisToDurationString(
      (1 - played) * duration * 1000
    );

    safeSetPlayerState({
      timePlayed: timePlayed,
      timeRemaining: `-${timeRemaining}`,
    });
  }, [played, ready]);

  useEffect(() => {
    if (fileId) {
      getSignedUrl({
        params: { id: fileId },
      });
    }
    if (!playerState.tempFile) {
      return;
    }

    setDefaultDuration(tempFile);
    setExpirationTimeout(tempFile?.expires - DateTime.now().toMillis());
  }, [tempFile, fileId]);

  useEffect(() => {
    if (!signedUrl) {
      return;
    }

    safeSetPlayerState({
      hasExpired: false,
      tempFile: signedUrl,
      url: signedUrl.temporaryUrl,
    });

    setDefaultDuration(signedUrl);

    setExpirationTimeout(signedUrl.expires - DateTime.now().toMillis());
  }, [signedUrl]);

  const handleOnPlaybackRateChange = speed => {
    safeSetPlayerState({ playbackRate: parseFloat(speed) });
  };

  const setDefaultDuration = (tempFile: TemporaryLinkResponse) => {
    if (!tempFile) {
      return;
    }

    // contentDuration is in seconds, format it to display in the player
    const durationDisplay = tempFile?.file?.contentDuration
      ? timeFormatter.millisToDurationString(tempFile?.file.contentDuration)
      : '--:--';
    const timePlayed = tempFile?.file?.contentDuration ? '00:00' : '--:--';

    safeSetPlayerState({
      url: tempFile.temporaryUrl,
      tempFile,
      timePlayed: timePlayed,
      timeRemaining: durationDisplay,
    });
  };

  const handlePlay = () => {
    // Check if we have a valid URL and if it has expired
    const currentUrl = signedUrl || tempFile;
    const currentExpires = currentUrl?.expires;
    const currentTime = DateTime.now().toMillis();

    // If we have an expiration time and it's expired, get a new signed URL
    if (currentExpires && currentExpires < currentTime) {
      const id = currentUrl?.file?.id || fileId;
      if (id) {
        getSignedUrl({
          params: { id },
        });
        // Don't start playing yet, wait for the new URL
        return;
      }
    } else {
      safeSetPlayerState({ playing: true });
    }
  };

  const handlePause = () => {
    safeSetPlayerState({ playing: false });
  };

  const handleSeekMouseDownSlider = () => {
    safeSetPlayerState({ seeking: true });
  };

  const handleSeekChangeSlider = (
    event: Event,
    newValue: number | number[]
  ) => {
    safeSetPlayerState({ played: newValue });
  };

  const handleSeekMouseUpSlider = () => {
    safeSetPlayerState({ seeking: false });
    playerRef.current.seekTo(playerState.played);
  };

  const handleProgress = state => {
    // We only want to update time slider if we are not currently seeking
    if (!playerState.seeking) {
      safeSetPlayerState(state);
    }
  };

  const handleDuration = duration => {
    const timeRemaining = timeFormatter.millisToDurationString(
      (1 - playerState.played) * duration * 1000
    );
    safeSetPlayerState({ duration, timeRemaining: `-${timeRemaining}` });
  };

  const setPlaying = playing => {
    if (playing) {
      // Use handlePlay to check for expiration before starting playback
      handlePlay();
    } else {
      safeSetPlayerState({ playing: false });
    }
  };

  const handleControlMouseDown = () => {
    if (!playerState.setup) {
      safeSetPlayerState({ setup: true });
    }
  };

  const handleError = e => {
    safeSetPlayerState({ error: true });
    console.error('Audio player error', e);
  };

  const handleReady = () => {
    safeSetPlayerState({ ready: true });
  };

  return (
    <div className="app">
      {setup && !disabled && (
        <ReactPlayer
          ref={playerRef}
          url={setup && playerState.url}
          className="react-player"
          width="0px"
          height="0px"
          playing={playing}
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onPlaybackRateChange={handleOnPlaybackRateChange}
          onError={handleError}
          onProgress={handleProgress}
          onDuration={handleDuration}
        />
      )}
      <Tooltip
        title={disabled ? 'Unsupported file type. Download file to listen' : ''}
      >
        <Box onMouseDown={handleControlMouseDown}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'min-content 1fr',
              alignItems: 'center',
              columnGap: '8px',
            }}
          >
            <Box>
              <IconButton
                disabled={disabled}
                aria-label={playing ? 'pause' : 'play'}
                onClick={() => setPlaying(!playing)}
              >
                {playing ? <PauseCircleIcon /> : <PlayCircleIcon />}
              </IconButton>
            </Box>

            <Box>
              <Slider
                disabled={disabled}
                aria-label="Volume"
                step={0.00000001}
                min={0}
                max={0.999999}
                value={played}
                onMouseDown={handleSeekMouseDownSlider}
                onChange={handleSeekChangeSlider}
                onMouseUp={handleSeekMouseUpSlider}
              />

              {(alwaysShowDownload || disabled) && (
                <a
                  href={playerState.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconButton>
                    <FileDownloadIcon />
                  </IconButton>
                </a>
              )}
            </Box>

            <Box>{/* Purposely left empty for layout purposes*/}</Box>
            {!disabled && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="bodySmall">
                  {playerState.timePlayed}
                </Typography>
                <Typography variant="bodySmall">
                  {playerState.timeRemaining}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Tooltip>
    </div>
  );
};

export default AudioPlayer;
